import { motion } from "framer-motion";
import {
  Video,
  Handshake,
  Users,
  Phone,
  Mail,
  ClipboardList,
  DollarSign,
  Clock,
  Calendar,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";
import { Button } from "./ui/button";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/dividelo";

const AGENT_BRINGS = [
  {
    icon: Users,
    label: "Cliente(s) y número de personas",
    detail: "Nombres completos, edades, relación entre solicitantes y país de destino.",
  },
  {
    icon: Phone,
    label: "Teléfonos de contacto",
    detail: "Número directo de cada solicitante para el seguimiento por WhatsApp.",
  },
  {
    icon: Mail,
    label: "Emails de cada solicitante",
    detail: "Para envío de accesos, formularios y comunicaciones oficiales del caso.",
  },
  {
    icon: ClipboardList,
    label: "Observaciones del caso",
    detail: "Historial migratorio, antecedentes relevantes, negaciones previas o particularidades.",
  },
  {
    icon: Handshake,
    label: "Persona responsable del trámite",
    detail: "Quién será el punto de contacto principal por parte del cliente y del agente.",
  },
  {
    icon: DollarSign,
    label: "Pago de la asesoría (si aplica)",
    detail: "Coordinamos el link de pago, cuotas y comisión del agente al cerrar la reunión.",
  },
];

const AGENT_MEETING_STEPS = [
  {
    n: "1",
    title: "El agente agenda la cita",
    desc: "Elige el día y la hora que mejor te acomode desde el calendario en vivo.",
  },
  {
    n: "2",
    title: "Reunión Zoom de 20 min",
    desc: "Nos cuentas el caso de tu cliente: personas, arraigo, historial y particularidades.",
  },
  {
    n: "3",
    title: "Divídelo activa el caso",
    desc: "Iniciamos la asesoría bajo tu dirección estratégica y con tu cliente listo para avanzar.",
  },
];

const AgentMeetingSection = () => {
  return (
    <section
      id="formulario"
      className="relative py-20 px-6 overflow-hidden bg-gradient-to-b from-white via-navy-50 to-white"
      data-testid="agent-meeting-section"
    >
      <div className="max-w-6xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs uppercase tracking-widest font-black mb-5"
            data-testid="meeting-section-badge"
          >
            <Video className="w-3.5 h-3.5" />
            ★ Solo agentes acreditados ★
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-navy-900 mb-5 leading-tight">
            Reunión Zoom con tu <span className="text-red-600">cliente y su caso</span>
          </h2>
          <p className="text-navy-700 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Cada caso es único y{" "}
            <strong className="text-navy-900">
              tu conocimiento del cliente es lo más valioso
            </strong>
            . Por eso, antes de abrir cualquier trámite,{" "}
            <strong className="text-red-600">
              nos reunimos contigo 20 minutos por Zoom
            </strong>{" "}
            para trabajar bajo tu dirección.
          </p>
        </motion.div>

        {/* Motivational alliance banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto mb-14"
          data-testid="alliance-banner"
        >
          <div className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-red-950 text-white rounded-3xl p-8 sm:p-10 shadow-2xl overflow-hidden">
            <div className="absolute top-4 right-6 text-white/10 text-6xl select-none">★</div>
            <div className="absolute bottom-4 left-8 text-white/10 text-5xl select-none">★</div>
            <div className="absolute top-1/2 right-1/4 text-white/5 text-8xl select-none">★</div>

            <div className="relative flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-red-500/25 border-2 border-red-400/50 flex items-center justify-center">
                <Handshake className="w-8 h-8 text-red-200" />
              </div>
              <div className="flex-1">
                <p className="text-red-300 text-xs uppercase tracking-widest font-black mb-3">
                  ★ Tú diriges · Nosotros ejecutamos ★
                </p>
                <h3 className="font-heading text-2xl sm:text-3xl font-black mb-4 leading-tight">
                  El agente es <span className="text-red-300">nuestro aliado estratégico</span>
                </h3>
                <p className="text-blue-100 text-base sm:text-lg leading-relaxed mb-4">
                  Tú conoces al cliente, tú generaste la confianza, tú preparaste el terreno. Nuestro rol es{" "}
                  <strong className="text-white">
                    tomar toda tu preparación y llevarla al siguiente nivel
                  </strong>{" "}
                  con nuestros ex cónsules, IA y adelanto de cita.
                </p>
                <p className="text-blue-100 text-base sm:text-lg leading-relaxed">
                  Cada asesoría avanza <strong className="text-white">bajo la dirección del trabajo que ya hiciste</strong> — no reinventamos, potenciamos. Por eso empezamos siempre con una reunión contigo.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Three steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14"
          data-testid="meeting-steps"
        >
          <p className="text-center text-navy-500 uppercase tracking-widest text-xs font-black mb-6">
            Así iniciamos cada caso
          </p>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {AGENT_MEETING_STEPS.map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative bg-white border-2 border-navy-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-red-200 transition-all"
                data-testid={`meeting-step-${step.n}`}
              >
                <div className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-gradient-to-br from-red-600 to-red-700 text-white font-black flex items-center justify-center shadow-lg">
                  {step.n}
                </div>
                <div className="pt-2">
                  <h4 className="font-heading text-lg font-black text-navy-900 mb-2">{step.title}</h4>
                  <p className="text-navy-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What agent brings to the meeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mb-14"
          data-testid="what-agent-brings"
        >
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs uppercase tracking-widest font-black mb-3">
              <ClipboardList className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              Prepárate para la reunión
            </span>
            <h3 className="font-heading text-2xl sm:text-3xl font-black text-navy-900 mb-2">
              Trae contigo estos <span className="text-red-600">6 datos clave</span>
            </h3>
            <p className="text-navy-600 max-w-2xl mx-auto text-sm sm:text-base">
              Con esta información activamos el caso el mismo día. No te preocupes si te falta algo — lo terminamos de armar en vivo.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AGENT_BRINGS.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white border border-navy-100 rounded-xl p-5 hover:shadow-md hover:border-red-200 transition-all"
                  data-testid={`agent-brings-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-heading font-black text-navy-900 text-sm mb-1 leading-tight">
                        {item.label}
                      </p>
                      <p className="text-navy-600 text-xs leading-relaxed">{item.detail}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Big CTA + Booking */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
          data-testid="booking-block"
        >
          <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-blue-900 text-white rounded-t-3xl p-8 sm:p-10 shadow-2xl overflow-hidden">
            {/* Decorative stars */}
            <div className="absolute top-6 left-6 text-white/10 text-6xl select-none pointer-events-none">★</div>
            <div className="absolute top-1/2 right-8 text-white/10 text-7xl select-none pointer-events-none">★</div>
            <div className="absolute bottom-6 left-1/4 text-white/10 text-5xl select-none pointer-events-none">★</div>

            <div className="relative grid md:grid-cols-[auto_1fr] gap-6 sm:gap-8 items-center">
              {/* Calendar illustration */}
              <div className="mx-auto md:mx-0 w-28 h-28 sm:w-32 sm:h-32 bg-white/95 rounded-2xl shadow-2xl flex flex-col items-center justify-center border-2 border-white/40" data-testid="booking-calendar-illustration">
                <div className="w-full bg-red-600 text-white text-[10px] uppercase tracking-widest font-black py-1 text-center rounded-t-xl">
                  Zoom · 20 min
                </div>
                <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-red-700 mt-2" />
                <p className="text-red-900 font-black text-xs mt-1">RESERVA HOY</p>
              </div>

              {/* CTA content */}
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 border border-white/30 text-white text-[11px] uppercase tracking-widest font-black mb-4">
                  <Clock className="w-3 h-3" />
                  20 minutos · Sin costo · Sin compromiso
                </div>
                <h3 className="font-heading text-3xl sm:text-4xl font-black mb-3 leading-tight">
                  Agenda tu cita Zoom ahora
                </h3>
                <p className="text-red-50 text-base sm:text-lg mb-6 leading-relaxed">
                  Elige el día y hora que mejor te acomode. Al confirmar, recibirás el link Zoom por email inmediatamente.
                </p>

                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  data-testid="booking-cta-primary"
                >
                  <Button
                    size="lg"
                    className="bg-white text-red-700 hover:bg-red-50 font-black px-8 py-6 shadow-2xl text-base sm:text-lg"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Reservar mi cita Zoom
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>

                <p className="text-red-100 text-xs mt-4 flex items-center justify-center md:justify-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Reservado por más de 500 agentes cada mes
                </p>
              </div>
            </div>
          </div>

          {/* Embedded booking widget */}
          <div className="bg-white border-2 border-red-200 border-t-0 rounded-b-3xl overflow-hidden shadow-2xl" data-testid="booking-widget-container">
            <div className="flex items-center justify-between px-6 py-3 bg-navy-50 border-b border-navy-100">
              <p className="text-xs text-navy-700 font-semibold flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-red-600" />
                Calendario en vivo · Reserva sin salir de la página
              </p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-red-600 hover:text-red-700 font-black uppercase tracking-widest hidden sm:inline-flex items-center gap-1"
                data-testid="booking-open-fullpage"
              >
                Pantalla completa <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <iframe
              src={BOOKING_URL}
              title="Agenda tu cita Zoom con Divídelo"
              className="w-full border-0 block"
              style={{ height: "820px", minHeight: "600px" }}
              data-testid="booking-iframe"
              allow="clipboard-write; camera; microphone"
              scrolling="yes"
            />
          </div>

          {/* Reassurance line */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-navy-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Confirmación instantánea por email</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Reprograma con un solo click</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Sin compromiso ni pago inicial</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AgentMeetingSection;
